import React from 'react';
import { Link } from 'react-router-dom';
import AuthenticationService from '../service/AuthenticationService.js';
import AuthLayout, { PasswordInput, FieldError, apiErrorMessage, finishSignIn, isValidEmail } from '../components/AuthLayout';
import { GoogleClientId, GoogleSignInOption } from '../components/GoogleSignInButton';

// Mirrors backend SignUpRequest: name 4-40, password 6-20. Codes are 6 digits; a new one every 30 s.
const MIN_PASSWORD = 6
const MAX_PASSWORD = 20
const RESEND_SECONDS = 30

// 0-3: length, letters + numbers, symbol or mixed case
const passwordScore = (pw) => {
    if (!pw) return 0
    let score = pw.length >= 8 ? 1 : 0
    if (/[a-zA-Z]/.test(pw) && /\d/.test(pw)) score++
    if (/[^a-zA-Z0-9]/.test(pw) || (/[a-z]/.test(pw) && /[A-Z]/.test(pw))) score++
    return Math.max(1, score)
}
const STRENGTH = ['', 'Weak', 'Good', 'Strong']

const Steps = ({ current }) => (
    <ol className='mv-auth-steps' aria-label='Sign-up steps'>
        {['Email', 'Verify & set password'].map((label, i) =>
            <li key={label} className={i + 1 === current ? 'is-current' : i + 1 < current ? 'is-done' : ''}
                aria-current={i + 1 === current ? 'step' : undefined}>
                <span className='mv-auth-step-num'>{i + 1}</span> {label}
            </li>
        )}
    </ol>
)

class RegisterUser extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            email: '',
            password: '',
            confirmPassword: '',
            userType: "user",
            errorMsg: '',
            fieldErrors: {},
            isLoading: false,
            isSending: false,
            isVerified: false,
            confirmationCode: '',
            notice: '',
            resendIn: 0
        };
        this.timer = null
    }

    componentWillUnmount() {
        clearInterval(this.timer)
    }

    onChange = (key) => (event) => {
        this.setState({ [key]: event.target.value })
    }

    onChangeCode = (event) => {
        this.setState({ confirmationCode: event.target.value.replace(/\D/g, '').slice(0, 6) })
    }

    startResendTimer = () => {
        clearInterval(this.timer)
        this.setState({ resendIn: RESEND_SECONDS })
        this.timer = setInterval(() => {
            this.setState(s => {
                if (s.resendIn <= 1) clearInterval(this.timer)
                return { resendIn: Math.max(0, s.resendIn - 1) }
            })
        }, 1000)
    }

    sendCode = (email, resend) => {
        this.setState({ isSending: true, fieldErrors: {}, errorMsg: '' })
        AuthenticationService.verifyEmail(email).then(() => {
            this.setState({
                email,
                isSending: false,
                isVerified: true,
                confirmationCode: resend ? '' : this.state.confirmationCode,
                notice: resend ? `We sent a new code to ${email}.` : ''
            })
            this.startResendTimer()
        }).catch(error => {
            this.setState({
                errorMsg: apiErrorMessage(error, 'We could not send the code. Please try again.'),
                isSending: false
            })
            // asked again too soon: show the countdown so they know when they can
            if (error?.response?.status === 429) this.startResendTimer()
        })
    }

    verifyEmail = (event) => {
        event.preventDefault();
        const email = this.state.email.trim()
        if (!isValidEmail(email)) {
            this.setState({ fieldErrors: { email: 'Please enter a valid email address.' }, errorMsg: '' })
            return
        }
        this.sendCode(email, false)
    }

    resendCode = () => {
        if (this.state.resendIn > 0 || this.state.isSending) return
        this.sendCode(this.state.email, true)
    }

    changeEmail = () => {
        clearInterval(this.timer)
        this.setState({ isVerified: false, notice: '', errorMsg: '', fieldErrors: {}, confirmationCode: '', resendIn: 0 })
    }

    register = (event) => {
        event.preventDefault();
        const { name, password, confirmPassword, confirmationCode } = this.state
        const fieldErrors = {}
        if (confirmationCode.length !== 6) fieldErrors.code = 'Enter the 6-digit code from your email.'
        if (name.trim().length < 4 || name.trim().length > 40) fieldErrors.name = 'Name must be 4 to 40 characters.'
        if (password.length < MIN_PASSWORD || password.length > MAX_PASSWORD) fieldErrors.password = `Password must be ${MIN_PASSWORD} to ${MAX_PASSWORD} characters.`
        if (!fieldErrors.password && password !== confirmPassword) fieldErrors.confirmPassword = 'Passwords do not match.'
        this.setState({ fieldErrors, errorMsg: '', notice: '' })
        if (Object.keys(fieldErrors).length > 0) return

        this.setState({ isLoading: true })
        const { email, userType } = this.state
        AuthenticationService.regiserUser({
            name: name.trim(),
            email,
            password,
            confirmPassword,
            userType,
            confirmationCode
        }).then(() => {
            // account created: sign straight in instead of asking them to log in again
            return AuthenticationService.loginApi({ usernameOrEmail: email, password }).then(res => {
                clearInterval(this.timer)
                finishSignIn(this.props.history, res.data)
            }).catch(() => {
                this.props.history.push({ pathname: '/login', state: { registered: true, email } })
            })
        }).catch(error => {
            this.setState({
                errorMsg: apiErrorMessage(error, 'Could not create your account. Please try again.'),
                isLoading: false
            })
        })
    }

    render() {
        return <GoogleClientId>{this.renderPage}</GoogleClientId>
    }

    // googleClientId: undefined while unknown, '' when Google sign-in is off
    renderPage = (googleClientId) => {
        const { name, email, password, confirmPassword, isLoading, isSending, errorMsg, fieldErrors, isVerified, confirmationCode, notice, resendIn } = this.state
        const footer = <>Already have an account? <Link className='mv-auth-link' to='/login'>Log in</Link></>

        if (!isVerified) {
            return (
                <AuthLayout title='Create your account'
                    subtitle='Join in seconds to shop, track orders and check out faster.'
                    steps={<Steps current={1} />}
                    footer={footer}>
                    <GoogleSignInOption clientId={googleClientId} text='signup_with' divider='or sign up with email'
                        onSuccess={data => finishSignIn(this.props.history, data)}
                        onError={message => this.setState({ errorMsg: message })} />

                    <form onSubmit={this.verifyEmail} className={'mv-auth-form' + (googleClientId !== '' ? ' after-alt' : '')} noValidate>
                        {errorMsg && <div className='mv-alert mv-alert-error' role='alert'>{errorMsg}</div>}
                        <div className='mv-field'>
                            <label className='mv-label' htmlFor='register-email'>Email</label>
                            <input id='register-email' className='mv-input' type='email' inputMode='email' autoComplete='email'
                                placeholder='you@example.com' value={email} onChange={this.onChange('email')}
                                aria-invalid={fieldErrors.email ? 'true' : undefined}
                                aria-describedby={fieldErrors.email ? 'register-email-error' : undefined} />
                            <FieldError id='register-email-error' message={fieldErrors.email} />
                        </div>
                        <button type='submit' className={`mv-btn mv-btn-primary mv-btn-block mv-auth-submit${isSending ? ' is-busy' : ''}`} disabled={isSending} aria-busy={isSending}>
                            {isSending ? 'Sending code…' : 'Send code'}
                        </button>
                        <p className='mv-auth-fine'>We'll email you a 6-digit code to confirm it's you. It's valid for 10 minutes.</p>
                    </form>
                </AuthLayout>
            )
        }

        const score = passwordScore(password)
        return (
            <AuthLayout title='Check your email' subtitle='Enter the code we sent, then choose your name and password.'
                steps={<Steps current={2} />} footer={footer}>
                <form onSubmit={this.register} className='mv-auth-form' noValidate>
                    {notice && !errorMsg && <div className='mv-alert mv-alert-success' role='status'>{notice}</div>}
                    {errorMsg && <div className='mv-alert mv-alert-error' role='alert'>{errorMsg}</div>}

                    <div className='mv-auth-email'>
                        <span>Code sent to <strong>{email}</strong></span>
                        <button type='button' className='mv-auth-link' onClick={this.changeEmail}>Change</button>
                    </div>

                    <div className='mv-field'>
                        <div className='mv-auth-row'>
                            <label className='mv-label' htmlFor='register-code'>6-digit code</label>
                            <button type='button' className='mv-auth-link' onClick={this.resendCode} disabled={resendIn > 0 || isSending}>
                                {isSending ? 'Sending…' : resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
                            </button>
                        </div>
                        <input id='register-code' className='mv-input mv-code-input' inputMode='numeric' autoComplete='one-time-code'
                            placeholder='••••••' value={confirmationCode} onChange={this.onChangeCode}
                            aria-invalid={fieldErrors.code ? 'true' : undefined}
                            aria-describedby={fieldErrors.code ? 'register-code-error' : undefined} />
                        <FieldError id='register-code-error' message={fieldErrors.code} />
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='register-name'>Your name</label>
                        <input id='register-name' className='mv-input' autoComplete='name' placeholder='e.g. Priya Raman'
                            value={name} onChange={this.onChange('name')}
                            aria-invalid={fieldErrors.name ? 'true' : undefined}
                            aria-describedby={fieldErrors.name ? 'register-name-error' : undefined} />
                        <FieldError id='register-name-error' message={fieldErrors.name} />
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='register-password'>Password</label>
                        <PasswordInput id='register-password' value={password} onChange={this.onChange('password')}
                            placeholder={`${MIN_PASSWORD} to ${MAX_PASSWORD} characters`} autoComplete='new-password'
                            invalid={!!fieldErrors.password} describedBy={fieldErrors.password ? 'register-password-error' : 'register-password-strength'} />
                        {password && !fieldErrors.password &&
                            <span className={'mv-strength mv-strength-' + score} id='register-password-strength'>
                                <span className='mv-strength-bar' aria-hidden='true'><i /><i /><i /></span>
                                {STRENGTH[score]} password
                            </span>}
                        <FieldError id='register-password-error' message={fieldErrors.password} />
                    </div>

                    <div className='mv-field'>
                        <label className='mv-label' htmlFor='register-confirm'>Confirm password</label>
                        <PasswordInput id='register-confirm' value={confirmPassword} onChange={this.onChange('confirmPassword')}
                            placeholder='Type it again' autoComplete='new-password'
                            invalid={!!fieldErrors.confirmPassword} describedBy={fieldErrors.confirmPassword ? 'register-confirm-error' : undefined} />
                        <FieldError id='register-confirm-error' message={fieldErrors.confirmPassword} />
                    </div>

                    <button type='submit' className={`mv-btn mv-btn-primary mv-btn-block mv-auth-submit${isLoading ? ' is-busy' : ''}`} disabled={isLoading} aria-busy={isLoading}>
                        {isLoading ? 'Creating your account…' : 'Create account'}
                    </button>
                </form>
            </AuthLayout>
        );
    }
}

export default RegisterUser;
